<template>
  <product-scenarios
    :value="adaptedValue"
    :is-mop="isMop"
    :product-type="productTypeSentinel"
    :rts-version="rtsVersionSentinel"
    :period1-captions="period1Captions"
    compact-scenarios
  ></product-scenarios>
</template>

<script>
// "Scenari di performance" (PerformanceScenarios). Uses
// components/ProductDetails/ProductScenarios.vue (prop-driven only, no Vuex/parent
// coupling).
//
// ProductScenarios.vue expects `value.period{1,2,3} = { scenariosYear, scenariosMin: {...},
// scenariosMax: {...} }` PLUS `value.currency` (read directly off the value prop, unlike
// ProductCosts.vue's separate `currency` prop) — not this page's PerformanceScenariosPayload
// (`{ period1/2/3, rows: [{scenarioKey, period{N}PercentageValue/period{N}PerformanceValue |
// period{N}PercentageMin/Max/period{N}PerformanceMin/Max, period1CaptionPeriod}], aie }`).
// `scenariosMin`/`scenariosMax` are dictionaries keyed by COMPOUND names — `performanceStress`/
// `percentageStress`/`performanceUnfavourable`/... (one row shows the absolute/monetary figure,
// another the return percentage — two independent numbers, not the same value formatted twice) PLUS
// `performanceAie`/`percentageAie` for the AIE sub-table — this adapter builds all of it from
// `payload.rows`/`payload.aie`.
//
// `isMop` is inferred the same way as CostsProfileSection.vue (any row carrying a Min/Max value).
// `productTypeSentinel`/`rtsVersionSentinel` exploit ProductScenarios.vue's own existing gates instead
// of adding new ones: it only ever checks `productType !== 4` (RamoV) to show/hide the AIE table —
// the backend already makes that call (`payload.aie` is null exactly when RamoV), so any non-4 value
// works when Aie is present; and `rtsVersion === 2` to prepend a "Minimum" row — the backend already
// makes THAT call too (`Rows` includes a Minimum entry only when at least one contributing Kpi has
// HasMinimumScenario), so deriving the sentinel from `rows` reproduces the exact same
// "Minimum first" ordering of the component's own hardcoded `elements` array
// — this adapter never needs to touch that ordering itself.
//
// Period1 "Termina dopo N anni/mesi" caption for an autocallable/callable IC product
// (PerformanceScenariosPayload.Rows[].Period1CaptionPeriod): one caption per scenario ROW, not a
// shared column header — the KID example in
// IC_SCENARIO_PERIODS_DESIGN.md shows "Moderato" terminating 3 years before "Stress"/"Sfavorevole"/
// "Favorevole" on the same product. `period1Captions` (below) is a `{ [scenarioKey]: caption|null }`
// map built from `payload.rows`, passed to `ProductScenarios.vue` as a prop
// (default `{}`) — same pattern as CostsProfileSection's `hideMaturityCosts`/`firstPeriodLabel`.
import ProductScenarios from '../../ProductDetails/ProductScenarios'
import { detailScenarioTerminationCaption } from '../../../utils/detailFieldFormat'

export default {
  name: 'performance-scenarios-section',
  components: {
    ProductScenarios,
  },
  inject: ['detailPage'],
  props: {
    payload: { type: Object, required: true },
  },
  computed: {
    isMop() {
      return this.payload.rows.some(
        (r) =>
          r.period1PercentageMin != null ||
          r.period1PercentageMax != null ||
          r.period2PercentageMin != null ||
          r.period2PercentageMax != null ||
          r.period3PercentageMin != null ||
          r.period3PercentageMax != null
      )
    },
    productTypeSentinel() {
      return this.payload.aie != null ? 0 : 4
    },
    rtsVersionSentinel() {
      return this.payload.rows.some((r) => r.scenarioKey === 'Minimum') ? 2 : 0
    },
    adaptedValue() {
      return {
        currency: this.detailPage.currency,
        period1: this.adaptPeriod(1),
        period2: this.adaptPeriod(2),
        period3: this.adaptPeriod(3),
      }
    },
    period1Captions() {
      const captions = {}
      this.payload.rows.forEach((row) => {
        captions[row.scenarioKey] = detailScenarioTerminationCaption(this, row.period1CaptionPeriod)
      })
      return captions
    },
  },
  methods: {
    adaptPeriod(periodNumber) {
      const scenariosMin = {}
      const scenariosMax = {}
      this.payload.rows.forEach((row) => {
        // Compound keys (`performanceStress`/`percentageStress`/...) — ProductScenarios.vue looks
        // these up as `scenariosMin[row.item.field]` where `row.item.field` is `'performance' + key`
        // / `'percentage' + key` (key = the scenario's own capitalized name, e.g. "Stress").
        const performanceKey = `performance${row.scenarioKey}`
        const percentageKey = `percentage${row.scenarioKey}`
        if (this.isMop) {
          scenariosMin[performanceKey] = row[`period${periodNumber}PerformanceMin`]
          scenariosMin[percentageKey] = row[`period${periodNumber}PercentageMin`]
          scenariosMax[performanceKey] = row[`period${periodNumber}PerformanceMax`]
          scenariosMax[percentageKey] = row[`period${periodNumber}PercentageMax`]
        } else {
          const performanceValue = row[`period${periodNumber}PerformanceValue`]
          const percentageValue = row[`period${periodNumber}PercentageValue`]
          scenariosMin[performanceKey] = performanceValue
          scenariosMin[percentageKey] = percentageValue
          scenariosMax[performanceKey] = performanceValue
          scenariosMax[percentageKey] = percentageValue
        }
      })
      if (this.payload.aie != null) {
        const aiePeriod = this.payload.aie[`period${periodNumber}`]
        scenariosMin.performanceAie = aiePeriod.performanceMin
        scenariosMin.percentageAie = aiePeriod.percentageMin
        scenariosMax.performanceAie = aiePeriod.performanceMax
        scenariosMax.percentageAie = aiePeriod.percentageMax
      }
      // Period3 is always "at RHP" by definition, same fix as CostsProfileSection.vue's own
      // adaptPeriod — ProductScenarios.vue's periodsHeaders() infers "this column is RHP" from
      // scenariosYear === 0 (an IBIPs-only DOXA convention), but IC's own Period3 carries the real
      // RHP figure instead (e.g. Kpi.Period3 = 5.05, not 0), which would otherwise show that raw
      // number in place of "RHP".
      const rawPeriod = this.payload[`period${periodNumber}`]
      const scenariosYear = periodNumber === 3 && rawPeriod != null ? 0 : rawPeriod
      return {
        scenariosYear,
        scenariosMin,
        scenariosMax,
      }
    },
  },
}
</script>
